import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination as MuiPagination,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemText,
  Avatar
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Inventory,
  History,
  CloudUpload,
  Close,
  Image as ImageIcon,
  Settings,
  LocalShipping,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import axios from 'axios';
import Title from '../../components/Title';
import {
  useGetSparePartsQuery,
  useCreateSparePartMutation,
  useUpdateSparePartMutation,
  useDeleteSparePartMutation,
  useUpdateSparePartStockMutation,
  useGetScooterTypesQuery,
  useGetWarehousesQuery,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
  useGetSparePartTypesQuery,
  useCreateSparePartTypeMutation,
  useUpdateSparePartTypeMutation,
  useDeleteSparePartTypeMutation,
  useGetSparePartOrdersQuery,
  useCreateSparePartOrderMutation,
  useUpdateSparePartOrderMutation
} from '../../redux/api/sparePartsApi';
import { useLazyGetWorkshopTasksQuery } from '../../redux/api/workshopTasksApi';
import { WorkshopTask, WorkshopTaskStatus, WorkshopTaskScooter, WorkshopTaskEmployee } from '../../resources/types/workshopTasksTypes';
import {
  SparePart,
  SparePartsCondition,
  SparePartsStockStatus,
  CreateSparePartBody,
  UpdateSparePartBody,
  SparePartType,
  SparePartOrder,
  SparePartOrderStatus
} from '../../resources/types/sparePartsTypes';
import { TableCell as MuiTableCell } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectCities } from '../../redux/slices/citiesSlice';

const Root = styled('div')({
  padding: 0,
  minHeight: 'calc(100vh - 180px)'
});

const FilterSection = styled(Box)({
  marginBottom: 24,
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  alignItems: 'center'
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.08)'
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.08)'
      : 'rgba(255, 255, 255, 0.1)'
  }`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
  padding: '12px 16px'
}));

const PaginationWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 24
});

const SparePartsPage = () => {
  const { t } = useTranslation();
  const auth = useSelector(selectAuth);
  const cities = useSelector(selectCities);
  const isSuperAdmin = useMemo(() => auth.info?.type === 'superAdmin', [auth.info?.type]);

  const [page, setPage] = useState(1);
  const [searchName, setSearchName] = useState('');
  const [statusFilter, setStatusFilter] = useState<SparePartsStockStatus | ''>('');
  const [conditionFilter, setConditionFilter] = useState<SparePartsCondition | ''>('');
  const [scooterTypeFilter, setScooterTypeFilter] = useState<string[]>([]);
  const [sparePartTypeFilter, setSparePartTypeFilter] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingSparePart, setEditingSparePart] = useState<SparePart | null>(null);
  const [formData, setFormData] = useState<Partial<CreateSparePartBody>>({
    name: '',
    code: '',
    supplier: t('spareParts.defaultSupplier'),
    condition: SparePartsCondition.NEW,
    sparePartType: '',
    price: 0,
    stockThresholdLow: 10,
    scooterTypes: [],
    photo: ''
  });
  const [stockAmount, setStockAmount] = useState(0);
  const [uploadedPhoto, setUploadedPhoto] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoModalSrc, setPhotoModalSrc] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingSparePartId, setDeletingSparePartId] = useState<string | null>(null);
  const [deleteCountdown, setDeleteCountdown] = useState(3);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [warehouseAmount, setWarehouseAmount] = useState(0);
  const [newWarehouseDialogOpen, setNewWarehouseDialogOpen] = useState(false);
  const [sparePartTypesDialogOpen, setSparePartTypesDialogOpen] = useState(false);
  const [editingSparePartType, setEditingSparePartType] = useState<SparePartType | null>(null);
  const [sparePartTypeName, setSparePartTypeName] = useState('');
  const [newWarehouseName, setNewWarehouseName] = useState('');
  const [newWarehouseLat, setNewWarehouseLat] = useState<number>(0);
  const [newWarehouseLon, setNewWarehouseLon] = useState<number>(0);
  const [editingWarehouse, setEditingWarehouse] = useState<{ _id: string; name: string; coords: { lat: number; lon: number } } | null>(null);
  const [generalQuantity, setGeneralQuantity] = useState(0);
  const [useGeneralQuantity, setUseGeneralQuantity] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [ordersListDialogOpen, setOrdersListDialogOpen] = useState(false);
  const [orderFormData, setOrderFormData] = useState({
    name: '',
    description: '',
    trackingNumber: '',
    expectedDeliveryDate: '',
    spareParts: [] as { sparePart: string; quantity: number }[]
  });

  const { data: sparePartsData, isLoading } = useGetSparePartsQuery({
    page,
    limit: 1000,
    name: searchName || undefined,
    status: statusFilter || undefined,
    condition: conditionFilter || undefined,
    sparePartType: sparePartTypeFilter || undefined
  });

  const filteredSpareParts = useMemo(() => {
    if (!sparePartsData?.data) return sparePartsData;
    
    let filtered = sparePartsData.data;
    if (conditionFilter) {
      filtered = filtered.filter(part => part.condition === conditionFilter);
    }
    if (scooterTypeFilter.length > 0) {
      filtered = filtered.filter(part => part.scooterTypes?.some(st => scooterTypeFilter.includes(st._id)));
    }
    
    return {
      data: filtered,
      pagination: sparePartsData.pagination
    };
  }, [sparePartsData, conditionFilter, scooterTypeFilter]);

  const { data: scooterTypes = [] } = useGetScooterTypesQuery();
  const { data: warehousesData = [] } = useGetWarehousesQuery();
  const { data: sparePartTypesData = [] } = useGetSparePartTypesQuery();
  const [createSparePartType, { isLoading: isCreatingSparePartType }] = useCreateSparePartTypeMutation();
  const [updateSparePartType, { isLoading: isUpdatingSparePartType }] = useUpdateSparePartTypeMutation();
  const [deleteSparePartType] = useDeleteSparePartTypeMutation();
  const [createSparePart] = useCreateSparePartMutation();
  const [updateSparePart] = useUpdateSparePartMutation();
  const [deleteSparePart] = useDeleteSparePartMutation();
  const [updateStock] = useUpdateSparePartStockMutation();
  const [fetchWorkshopTasks, { data: workshopTasksData, isLoading: isHistoryLoading }] = useLazyGetWorkshopTasksQuery();
  const [createWarehouse, { isLoading: isCreatingWarehouse }] = useCreateWarehouseMutation();
  const { data: ordersData, isLoading: isOrdersLoading } = useGetSparePartOrdersQuery({ status: SparePartOrderStatus.IN_DELIVERY, limit: 100 });
  const [createOrder, { isLoading: isCreatingOrder }] = useCreateSparePartOrderMutation();
  const [updateOrder] = useUpdateSparePartOrderMutation();

  const sparePartUsageHistory = useMemo(() => {
    if (!workshopTasksData?.data || !editingSparePart) return [];
    
    return workshopTasksData.data.filter(task => 
      task.spareParts?.some(sp => sp.id?._id === editingSparePart._id)
    ).map(task => {
      const usedPart = task.spareParts?.find(sp => sp.id?._id === editingSparePart._id);
      return {
        _id: task._id,
        task,
        qty: usedPart?.qty || 0
      };
    });
  }, [workshopTasksData?.data, editingSparePart]);
  const [updateWarehouse, { isLoading: isUpdatingWarehouse }] = useUpdateWarehouseMutation();

  const allWarehouses = useMemo(() => {
    const warehouseMap: Record<string, { id: string; name: string }> = {};
    
    warehousesData.forEach(wh => {
      warehouseMap[wh._id] = { id: wh._id, name: wh.name };
    });
    
    if (sparePartsData?.data) {
      sparePartsData.data.forEach(part => {
        if (part.stockInWarehouse) {
          Object.entries(part.stockInWarehouse).forEach(([warehouseId, warehouse]) => {
            if (!warehouseMap[warehouseId]) {
              warehouseMap[warehouseId] = { id: warehouseId, name: warehouse.name || warehouseId };
            }
          });
        }
        if (part.stock) {
          Object.keys(part.stock).forEach(warehouseId => {
            if (!warehouseMap[warehouseId]) {
              const wh = warehousesData.find(w => w._id === warehouseId);
              warehouseMap[warehouseId] = { id: warehouseId, name: wh?.name || warehouseId };
            }
          });
        }
      });
    }
    
    return Object.values(warehouseMap);
  }, [sparePartsData?.data, warehousesData]);

  const handleCreateWarehouse = async () => {
    if (!newWarehouseName.trim()) return;
    
    try {
      const result = await createWarehouse({ 
        name: newWarehouseName.trim(),
        coords: { lat: newWarehouseLat, lon: newWarehouseLon }
      }).unwrap();
      console.log('Warehouse created successfully:', result);
      setNewWarehouseName('');
      setNewWarehouseLat(0);
      setNewWarehouseLon(0);
      setNewWarehouseDialogOpen(false);
      if (result?._id) {
        setSelectedWarehouse(result._id);
      }
    } catch (error: any) {
      console.error('Failed to create warehouse:', error);
      const errorMessage = error?.data?.message || error?.message || t('spareParts.unknownError');
      alert(t('spareParts.warehouseCreateError', { error: errorMessage }));
    }
  };

  const handleEditWarehouse = (warehouse: { _id: string; name: string; coords?: { lat: number; lon: number } }) => {
    setEditingWarehouse({
      _id: warehouse._id,
      name: warehouse.name,
      coords: warehouse.coords || { lat: 0, lon: 0 }
    });
    setNewWarehouseName(warehouse.name);
    setNewWarehouseLat(warehouse.coords?.lat || 0);
    setNewWarehouseLon(warehouse.coords?.lon || 0);
    setNewWarehouseDialogOpen(true);
  };

  const handleUpdateWarehouse = async () => {
    if (!editingWarehouse || !newWarehouseName.trim()) return;
    
    try {
      await updateWarehouse({
        id: editingWarehouse._id,
        body: {
          name: newWarehouseName.trim(),
          coords: { lat: newWarehouseLat, lon: newWarehouseLon }
        }
      }).unwrap();
      setNewWarehouseName('');
      setNewWarehouseLat(0);
      setNewWarehouseLon(0);
      setEditingWarehouse(null);
      setNewWarehouseDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to update warehouse:', error);
      const errorMessage = error?.data?.message || error?.message || t('spareParts.unknownError');
      alert(t('spareParts.warehouseUpdateError', { error: errorMessage }));
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (deleteDialogOpen && deleteCountdown > 0) {
      timer = setTimeout(() => {
        setDeleteCountdown(deleteCountdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [deleteDialogOpen, deleteCountdown]);

  const handleCreateSparePartType = async () => {
    if (!sparePartTypeName.trim()) return;
    try {
      await createSparePartType({ name: sparePartTypeName.trim() }).unwrap();
      setSparePartTypeName('');
    } catch (error: any) {
      console.error('Failed to create spare part type:', error);
      alert(t('spareParts.typeCreateError', { error: error?.data?.message || error?.message || t('spareParts.unknownError') }));
    }
  };

  const handleUpdateSparePartType = async () => {
    if (!editingSparePartType || !sparePartTypeName.trim()) return;
    try {
      await updateSparePartType({ id: editingSparePartType._id, body: { name: sparePartTypeName.trim() } }).unwrap();
      setEditingSparePartType(null);
      setSparePartTypeName('');
    } catch (error: any) {
      console.error('Failed to update spare part type:', error);
      alert(t('spareParts.typeUpdateError', { error: error?.data?.message || error?.message || t('spareParts.unknownError') }));
    }
  };

  const handleDeleteSparePartType = async (id: string) => {
    if (!window.confirm(t('spareParts.confirmDeleteType'))) return;
    try {
      await deleteSparePartType(id).unwrap();
    } catch (error: any) {
      console.error('Failed to delete spare part type:', error);
      alert(t('spareParts.typeDeleteError', { error: error?.data?.message || error?.message || t('spareParts.unknownError') }));
    }
  };

  const handleOpenDialog = (sparePart?: SparePart) => {
    if (sparePart) {
      setEditingSparePart(sparePart);
      const sparePartTypeId = typeof sparePart.sparePartType === 'object' 
        ? sparePart.sparePartType?._id 
        : sparePart.sparePartType;
      setFormData({
        name: sparePart.name,
        code: sparePart.code || '',
        supplier: sparePart.supplier,
        condition: sparePart.condition,
        sparePartType: sparePartTypeId || '',
        price: sparePart.price,
        stockThresholdLow: sparePart.stockThresholdLow || 10,
        scooterTypes: sparePart.scooterTypes?.map(st => st._id) || [],
        photo: sparePart.photo || ''
      });
      setUploadedPhoto(sparePart.photo || '');
    } else {
      setEditingSparePart(null);
      setFormData({
        name: '',
        code: '',
        supplier: t('spareParts.defaultSupplier'),
        condition: SparePartsCondition.NEW,
        sparePartType: '',
        price: 0,
        stockThresholdLow: 10,
        scooterTypes: [],
        photo: ''
      });
      setUploadedPhoto('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSparePart(null);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/files/persistent`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const photoPath = response.data.path;
      setUploadedPhoto(photoPath);
      setFormData({ ...formData, photo: photoPath });
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert(t('spareParts.photoUploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setUploadedPhoto('');
    setFormData({ ...formData, photo: '' });
  };

  const handleSave = async () => {
    try {
      const saveData = { ...formData, photo: uploadedPhoto };
      
      if (!saveData.stockInWarehouse || !saveData.stockWarehouse) {
        delete saveData.stockInWarehouse;
        delete saveData.stockWarehouse;
      }
      
      console.log('=== SPARE PART SAVE DEBUG ===');
      console.log('formData.code:', formData.code);
      console.log('saveData.code:', saveData.code);
      console.log('Full saveData:', JSON.stringify(saveData, null, 2));
      
      let result;
      if (editingSparePart) {
        result = await updateSparePart({
          id: editingSparePart._id,
          body: saveData as UpdateSparePartBody
        }).unwrap();
      } else {
        result = await createSparePart(saveData as CreateSparePartBody).unwrap();
      }
      
      console.log('Backend response:', result);
      console.log('Response code field:', result?.code);
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save spare part:', error);
    }
  };

  const handleOpenPhotoModal = (photoPath: string) => {
    setPhotoModalSrc(`${process.env.REACT_APP_API_URL || ''}/public/${photoPath}`);
    setPhotoModalOpen(true);
  };

  const handleOpenDeleteDialog = (id: string) => {
    setDeletingSparePartId(id);
    setDeleteCountdown(3);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingSparePartId(null);
    setDeleteCountdown(3);
  };

  const handleConfirmDelete = async () => {
    if (!deletingSparePartId) return;
    try {
      await deleteSparePart(deletingSparePartId).unwrap();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Failed to delete spare part:', error);
    }
  };

  const handleOpenStockDialog = (sparePart: SparePart) => {
    setEditingSparePart(sparePart);
    setStockAmount(sparePart.stockCurrentCity?.available || 0);
    setGeneralQuantity(sparePart.stockCurrentCity?.available || 0);
    setUseGeneralQuantity(false);
    setSelectedWarehouse('');
    setWarehouseAmount(0);
    setStockDialogOpen(true);
  };

  const handleUpdateStock = async () => {
    if (!editingSparePart) return;
    try {
      let result;
      if (useGeneralQuantity) {
        console.log('Updating general quantity:', generalQuantity);
        result = await updateStock({
          sparePartId: editingSparePart._id,
          body: {
            setAvailable: generalQuantity,
            lastSupply: new Date().toISOString()
          }
        }).unwrap();
      } else {
        const selectedWarehouseData = warehousesData.find(w => w._id === selectedWarehouse);
        console.log('Updating stock with warehouse:', selectedWarehouse, 'name:', selectedWarehouseData?.name, 'amount:', warehouseAmount);
        result = await updateStock({
          sparePartId: editingSparePart._id,
          body: {
            stockWarehouse: selectedWarehouse,
            setAvailable: warehouseAmount,
            lastSupply: new Date().toISOString()
          }
        }).unwrap();
      }
      console.log('Stock update result - stock field:', JSON.stringify(result?.stock, null, 2));
      console.log('Stock update result - full:', result);
      setStockDialogOpen(false);
      setSelectedWarehouse('');
      setWarehouseAmount(0);
      setGeneralQuantity(0);
      setUseGeneralQuantity(false);
    } catch (error: any) {
      console.error('Failed to update stock:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      const errorMessage = error?.data?.message || error?.message || t('spareParts.unknownError');
      alert(t('spareParts.stockUpdateError', { error: errorMessage }));
    }
  };

  const handleOpenHistoryDialog = (sparePart: SparePart) => {
    setEditingSparePart(sparePart);
    fetchWorkshopTasks({ 
      status: WorkshopTaskStatus.DONE,
      limit: 1000,
      dateFrom: '2020-01-01',
      dateTo: new Date().toISOString().split('T')[0]
    });
    setHistoryDialogOpen(true);
  };

  const handleToggleScooterType = (typeId: string) => {
    const currentTypes = formData.scooterTypes || [];
    if (currentTypes.includes(typeId)) {
      setFormData({ ...formData, scooterTypes: currentTypes.filter(id => id !== typeId) });
    } else {
      setFormData({ ...formData, scooterTypes: [...currentTypes, typeId] });
    }
  };

  const handleOpenOrderDialog = () => {
    setOrderFormData({
      name: '',
      description: '',
      trackingNumber: '',
      expectedDeliveryDate: '',
      spareParts: []
    });
    setOrderDialogOpen(true);
  };

  const handleAddSparePartToOrder = (sparePartId: string) => {
    const existing = orderFormData.spareParts.find(sp => sp.sparePart === sparePartId);
    if (!existing) {
      setOrderFormData({
        ...orderFormData,
        spareParts: [...orderFormData.spareParts, { sparePart: sparePartId, quantity: 1 }]
      });
    }
  };

  const handleRemoveSparePartFromOrder = (sparePartId: string) => {
    setOrderFormData({
      ...orderFormData,
      spareParts: orderFormData.spareParts.filter(sp => sp.sparePart !== sparePartId)
    });
  };

  const handleUpdateSparePartQuantity = (sparePartId: string, quantity: number) => {
    setOrderFormData({
      ...orderFormData,
      spareParts: orderFormData.spareParts.map(sp => 
        sp.sparePart === sparePartId ? { ...sp, quantity: Math.max(1, quantity) } : sp
      )
    });
  };

  const handleCreateOrder = async () => {
    if (!orderFormData.name.trim() || orderFormData.spareParts.length === 0) {
      alert(t('spareParts.orderValidation'));
      return;
    }

    try {
      await createOrder({
        name: orderFormData.name.trim(),
        description: orderFormData.description.trim() || undefined,
        trackingNumber: orderFormData.trackingNumber.trim() || undefined,
        expectedDeliveryDate: orderFormData.expectedDeliveryDate || undefined,
        spareParts: orderFormData.spareParts,
        status: 'IN_DELIVERY'
      }).unwrap();
      setOrderDialogOpen(false);
      setOrderFormData({
        name: '',
        description: '',
        trackingNumber: '',
        expectedDeliveryDate: '',
        spareParts: []
      });
    } catch (error: any) {
      console.error('Failed to create order:', error);
      alert(t('spareParts.orderCreateError', { error: error?.data?.message || error?.message || t('spareParts.unknownError') }));
    }
  };

  const handleDeliverOrder = async (orderId: string) => {
    try {
      await updateOrder({
        id: orderId,
        body: {
          status: 'DELIVERED',
          factDeliveryDate: new Date().toISOString().split('T')[0]
        }
      }).unwrap();
    } catch (error: any) {
      console.error('Failed to mark order as delivered:', error);
      alert(t('spareParts.genericError', { error: error?.data?.message || error?.message || t('spareParts.unknownError') }));
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!window.confirm(t('spareParts.confirmCancelOrder'))) return;
    try {
      await updateOrder({
        id: orderId,
        body: { status: 'CANCELED' }
      }).unwrap();
    } catch (error: any) {
      console.error('Failed to cancel order:', error);
      alert(t('spareParts.genericError', { error: error?.data?.message || error?.message || t('spareParts.unknownError') }));
    }
  };

  const getStatusColor = (status?: SparePartsStockStatus) => {
    switch (status) {
      case 'IN_STOCK':
        return 'success';
      case 'LOW_STOCK':
        return 'warning';
      case 'OUT_OF_STOCK':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status?: SparePartsStockStatus) => {
    switch (status) {
      case 'IN_STOCK':
        return t('spareParts.statusInStock');
      case 'LOW_STOCK':
        return t('spareParts.statusLowStock');
      case 'OUT_OF_STOCK':
        return t('spareParts.statusOutOfStock');
      default:
        return '-';
    }
  };

  const getSparePartTypeLabel = (sparePartType?: SparePartType | string) => {
    if (!sparePartType) return '-';
    if (typeof sparePartType === 'object') return sparePartType.name;
    const foundType = sparePartTypesData.find(t => t._id === sparePartType);
    return foundType?.name || sparePartType || '-';
  };

  return (
    <Root>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Title>{t('spareParts.title')}</Title>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<LocalShipping />}
            onClick={() => setOrdersListDialogOpen(true)}
          >
            {t('spareParts.ordersBtn')}
            {ordersData?.data && ordersData.data.length > 0 && (
              <Chip 
                label={ordersData.data.length} 
                size="small" 
                color="primary" 
                sx={{ ml: 1, minWidth: 24, height: 20 }} 
              />
            )}
          </Button>
          {isSuperAdmin && (
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setSparePartTypesDialogOpen(true)}
            >
              {t('spareParts.partTypesBtn')}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            {t('spareParts.addPartBtn')}
          </Button>
        </Box>
      </Box>

      <FilterSection>
        <TextField
          label={t('spareParts.searchByName')}
          value={searchName}
          onChange={e => setSearchName(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('spareParts.status')}</InputLabel>
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as SparePartsStockStatus | '')}
            label={t('spareParts.status')}
          >
            <MenuItem value="">{t('spareParts.all')}</MenuItem>
            <MenuItem value="IN_STOCK">{t('spareParts.statusInStock')}</MenuItem>
            <MenuItem value="LOW_STOCK">{t('spareParts.statusLowStock')}</MenuItem>
            <MenuItem value="OUT_OF_STOCK">{t('spareParts.statusOutOfStock')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('spareParts.filterType')}</InputLabel>
          <Select
            value={conditionFilter}
            onChange={e => setConditionFilter(e.target.value as SparePartsCondition | '')}
            label={t('spareParts.filterType')}
          >
            <MenuItem value="">{t('spareParts.all')}</MenuItem>
            <MenuItem value="NEW">{t('spareParts.conditionNewPlural')}</MenuItem>
            <MenuItem value="USED">{t('spareParts.conditionUsed')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>{t('spareParts.scooterType')}</InputLabel>
          <Select
            multiple
            value={scooterTypeFilter}
            onChange={e => setScooterTypeFilter(typeof e.target.value === 'string' ? [] : e.target.value)}
            label={t('spareParts.scooterType')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(typeId => {
                  const type = scooterTypes.find(t => t._id === typeId);
                  return (
                    <Chip key={typeId} label={type?.name} size="small" />
                  );
                })}
              </Box>
            )}
          >
            {scooterTypes.map(type => (
              <MenuItem key={type._id} value={type._id}>
                <Checkbox checked={scooterTypeFilter.includes(type._id)} />
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('spareParts.partType')}</InputLabel>
          <Select
            value={sparePartTypeFilter}
            onChange={e => setSparePartTypeFilter(e.target.value as string)}
            label={t('spareParts.partType')}
          >
            <MenuItem value="">{t('spareParts.all')}</MenuItem>
            {sparePartTypesData.map(type => (
              <MenuItem key={type._id} value={type._id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterSection>

      <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : !filteredSpareParts?.data || filteredSpareParts.data.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              {t('spareParts.noPartsFound')}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('spareParts.colPhoto')}</TableHeaderCell>
                <TableHeaderCell>{t('spareParts.name')}</TableHeaderCell>
                <TableHeaderCell>{t('spareParts.colCode')}</TableHeaderCell>
                <TableHeaderCell>{t('spareParts.scooterType')}</TableHeaderCell>
                <TableHeaderCell>{t('spareParts.colSupplier')}</TableHeaderCell>
                {isSuperAdmin && <TableHeaderCell>{t('spareParts.colPrice')}</TableHeaderCell>}
                <TableHeaderCell>{t('spareParts.colCondition')}</TableHeaderCell>
                <TableHeaderCell>{t('spareParts.colStock')}</TableHeaderCell>
                <TableHeaderCell>{t('spareParts.status')}</TableHeaderCell>
                <TableHeaderCell>{t('spareParts.colActions')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSpareParts.data.map(sparePart => (
                <StyledTableRow key={sparePart._id}>
                  <StyledTableCell>
                    {sparePart.photo ? (
                      <Box
                        onClick={() => handleOpenPhotoModal(sparePart.photo!)}
                        sx={{
                          width: 60,
                          height: 48,
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: theme => theme.palette.mode === 'light' ? '#f5f5f5' : '#2a2a2a',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                      >
                        <img
                          src={`${process.env.REACT_APP_API_URL || ''}/public/${sparePart.photo}`}
                          alt={sparePart.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 60,
                          height: 48,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: theme => theme.palette.mode === 'light' ? '#f5f5f5' : '#2a2a2a'
                        }}
                      >
                        <ImageIcon fontSize="small" />
                      </Box>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>{sparePart.name}</StyledTableCell>
                  <StyledTableCell>{sparePart.code || '-'}</StyledTableCell>
                  <StyledTableCell>
                    {sparePart.scooterTypes && sparePart.scooterTypes.length > 0 ? (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {sparePart.scooterTypes.map(type => (
                          <Chip
                            key={type._id}
                            label={type.name}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </StyledTableCell>
                  <StyledTableCell>{sparePart.supplier || '-'}</StyledTableCell>
                  {isSuperAdmin && <StyledTableCell>${sparePart.price}</StyledTableCell>}
                  <StyledTableCell>
                    {sparePart.condition === SparePartsCondition.NEW ? t('spareParts.conditionNew') : t('spareParts.conditionUsed')}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box>
                      {(() => {
                        const stockInWarehouseEntries = sparePart.stockInWarehouse ? Object.entries(sparePart.stockInWarehouse) : [];
                        const stockEntries = sparePart.stock ? Object.entries(sparePart.stock) : [];
                        const hasStockInWarehouse = stockInWarehouseEntries.length > 0;
                        const entriesToUse = hasStockInWarehouse ? stockInWarehouseEntries : stockEntries;
                        const totalStock = entriesToUse.reduce((sum, [, data]: [string, any]) => {
                          if (typeof data === 'number') return sum + data;
                          return sum + (data.available || 0);
                        }, 0);
                        const inDelivery = sparePart.stockCurrentCity?.inDelivery || 0;
                        return (
                          <>
                            <Typography variant="body2" fontWeight={600}>
                              {totalStock}
                            </Typography>
                            {inDelivery > 0 && (
                              <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                <LocalShipping sx={{ fontSize: 14, color: 'primary.main' }} />
                                <Typography variant="caption" color="primary.main" fontWeight={500}>
                                  {t('spareParts.inTransit', { count: inDelivery })}
                                </Typography>
                              </Box>
                            )}
                            {entriesToUse.length > 0 && (
                              <Box mt={0.5}>
                                {entriesToUse.map(([warehouseId, stockData]: [string, any]) => {
                                  const warehouseFromApi = warehousesData.find(w => w._id === warehouseId);
                                  const warehouseFromAll = allWarehouses.find(w => w.id === warehouseId);
                                  const cityMatch = cities?.items?.find((c: any) => c._id === warehouseId);
                                  const warehouseName = warehouseFromApi?.name || warehouseFromAll?.name || cityMatch?.cityName || (stockData?.name) || t('spareParts.warehouseFallback');
                                  const availableQty = typeof stockData === 'number' ? stockData : (stockData?.available || 0);
                                  return (
                                    <Typography key={warehouseId} variant="caption" display="block" color="textSecondary">
                                      {warehouseName}: {availableQty}
                                    </Typography>
                                  );
                                })}
                              </Box>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={getStatusLabel(sparePart.status)}
                      color={getStatusColor(sparePart.status)}
                      size="small"
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenHistoryDialog(sparePart)}
                        title={t('spareParts.usageHistory')}
                      >
                        <History fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenStockDialog(sparePart)}
                        title={t('spareParts.updateStock')}
                      >
                        <Inventory fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(sparePart)}
                        title={t('spareParts.edit')}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(sparePart._id)}
                        title={t('spareParts.delete')}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {filteredSpareParts?.pagination && filteredSpareParts.pagination.totalPages > 1 && (
        <PaginationWrapper>
          <MuiPagination
            count={filteredSpareParts.pagination.totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </PaginationWrapper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSparePart ? t('spareParts.editPartTitle') : t('spareParts.addPartBtn')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t('spareParts.name')}
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('spareParts.colCode')}
              value={formData.code}
              onChange={e => setFormData({ ...formData, code: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t('spareParts.partType')}</InputLabel>
              <Select
                value={formData.sparePartType || ''}
                onChange={e =>
                  setFormData({ ...formData, sparePartType: e.target.value as string })
                }
                label={t('spareParts.partType')}
              >
                {sparePartTypesData.map(type => (
                  <MenuItem key={type._id} value={type._id}>
                    {type.name}
                  </MenuItem>
                ))}
                {formData.sparePartType && !sparePartTypesData.find(spt => spt._id === formData.sparePartType) && (
                  <MenuItem value={formData.sparePartType}>
                    {t('spareParts.deprecatedOption', { value: formData.sparePartType })}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <TextField
              label={t('spareParts.lowStockThreshold')}
              type="number"
              value={formData.stockThresholdLow || 10}
              onChange={e =>
                setFormData({ ...formData, stockThresholdLow: parseInt(e.target.value) || 10 })
              }
              fullWidth
              helperText={t('spareParts.lowStockThresholdHelper')}
            />
            <TextField
              label={t('spareParts.colSupplier')}
              value={formData.supplier}
              onChange={e => setFormData({ ...formData, supplier: e.target.value })}
              fullWidth
            />
            {isSuperAdmin && (
              <TextField
                label={t('spareParts.priceField')}
                type="number"
                value={formData.price}
                onChange={e =>
                  setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })
                }
                fullWidth
                required
              />
            )}
            <FormControl fullWidth>
              <InputLabel>{t('spareParts.colCondition')}</InputLabel>
              <Select
                value={formData.condition}
                onChange={e =>
                  setFormData({
                    ...formData,
                    condition: e.target.value as SparePartsCondition
                  })
                }
                label={t('spareParts.colCondition')}
              >
                <MenuItem value={SparePartsCondition.NEW}>{t('spareParts.conditionNew')}</MenuItem>
                <MenuItem value={SparePartsCondition.USED}>{t('spareParts.conditionUsed')}</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">{t('spareParts.partPhoto')}</Typography>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={uploading}
                  size="small"
                  startIcon={<CloudUpload />}
                >
                  {uploading ? t('spareParts.uploading') : (uploadedPhoto ? t('spareParts.changePhoto') : t('spareParts.uploadPhoto'))}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handlePhotoUpload}
                  />
                </Button>
              </Box>
              {uploadedPhoto && (
                <Box display="flex" alignItems="center" gap={2} p={2} sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: theme => theme.palette.mode === 'light' ? '#f5f5f5' : '#2a2a2a'
                }}>
                  <Avatar
                    src={`${process.env.REACT_APP_API_URL || ''}/public/${uploadedPhoto}`}
                    variant="rounded"
                    sx={{ width: 80, height: 80 }}
                  >
                    <ImageIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" noWrap>
                      {uploadedPhoto.split('/').pop()}
                    </Typography>
                  </Box>
                  <IconButton onClick={handleRemovePhoto} size="small" color="error" title={t('spareParts.deletePhoto')}>
                    <Close />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('spareParts.scooterTypesLabel')}
              </Typography>
              <FormGroup>
                {scooterTypes.map(type => (
                  <FormControlLabel
                    key={type._id}
                    control={
                      <Checkbox
                        checked={formData.scooterTypes?.includes(type._id) || false}
                        onChange={() => handleToggleScooterType(type._id)}
                      />
                    }
                    label={type.name}
                  />
                ))}
              </FormGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('spareParts.cancel')}</Button>
          <Button onClick={handleSave} variant="contained">
            {t('spareParts.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stockDialogOpen} onClose={() => setStockDialogOpen(false)}>
        <DialogTitle>{t('spareParts.updateStock')}</DialogTitle>
        <DialogContent>
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useGeneralQuantity}
                  onChange={e => setUseGeneralQuantity(e.target.checked)}
                />
              }
              label={t('spareParts.updateGeneralQuantity')}
            />
            
            {!useGeneralQuantity ? (
              <>
                <Box display="flex" gap={1} alignItems="flex-end">
                  <FormControl fullWidth>
                    <InputLabel>{t('spareParts.selectWarehouse')}</InputLabel>
                    <Select
                      value={selectedWarehouse}
                      onChange={e => {
                        const warehouseId = e.target.value;
                        setSelectedWarehouse(warehouseId);
                        setWarehouseAmount(0);
                      }}
                      label={t('spareParts.selectWarehouse')}
                    >
                      {warehousesData.map(warehouse => (
                        <MenuItem key={warehouse._id} value={warehouse._id}>
                          {warehouse.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {isSuperAdmin && (
                    <>
                      <IconButton 
                        onClick={() => {
                          const warehouse = warehousesData.find(w => w._id === selectedWarehouse);
                          if (warehouse) {
                            handleEditWarehouse(warehouse);
                          }
                        }} 
                        color="primary"
                        title={t('spareParts.editWarehouse')}
                        sx={{ mb: 0.5 }}
                        disabled={!selectedWarehouse || !warehousesData.find(w => w._id === selectedWarehouse)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        onClick={() => {
                          setEditingWarehouse(null);
                          setNewWarehouseName('');
                          setNewWarehouseLat(0);
                          setNewWarehouseLon(0);
                          setNewWarehouseDialogOpen(true);
                        }} 
                        color="primary"
                        title={t('spareParts.addNewWarehouse')}
                        sx={{ mb: 0.5 }}
                      >
                        <Add />
                      </IconButton>
                    </>
                  )}
                </Box>
                
                {allWarehouses.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    {t('spareParts.noWarehouses')}
                  </Typography>
                )}
                
                <TextField
                  label={t('spareParts.warehouseQuantity')}
                  type="number"
                  value={warehouseAmount}
                  onChange={e => setWarehouseAmount(parseInt(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </>
            ) : (
              <TextField
                label={t('spareParts.generalQuantity')}
                type="number"
                value={generalQuantity}
                onChange={e => setGeneralQuantity(parseInt(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 0 }}
                helperText={t('spareParts.currentQuantity', { count: editingSparePart?.stockCurrentCity?.available || 0 })}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setStockDialogOpen(false);
            setSelectedWarehouse('');
            setWarehouseAmount(0);
            setGeneralQuantity(0);
            setUseGeneralQuantity(false);
          }}>{t('spareParts.cancel')}</Button>
          <Button 
            onClick={handleUpdateStock} 
            variant="contained" 
            disabled={!useGeneralQuantity && !selectedWarehouse}
          >
            {t('spareParts.update')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newWarehouseDialogOpen} onClose={() => {
        setNewWarehouseDialogOpen(false);
        setNewWarehouseName('');
        setNewWarehouseLat(0);
        setNewWarehouseLon(0);
        setEditingWarehouse(null);
      }}>
        <DialogTitle>{editingWarehouse ? t('spareParts.editWarehouse') : t('spareParts.addNewWarehouse')}</DialogTitle>
        <DialogContent>
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <TextField
              label={t('spareParts.warehouseName')}
              value={newWarehouseName}
              onChange={e => setNewWarehouseName(e.target.value)}
              fullWidth
              autoFocus
              placeholder={t('spareParts.warehouseNamePlaceholder')}
            />
            <Box display="flex" gap={2}>
              <TextField
                label={t('spareParts.latitude')}
                type="number"
                value={newWarehouseLat}
                onChange={e => setNewWarehouseLat(parseFloat(e.target.value) || 0)}
                fullWidth
                inputProps={{ step: 0.000001 }}
              />
              <TextField
                label={t('spareParts.longitude')}
                type="number"
                value={newWarehouseLon}
                onChange={e => setNewWarehouseLon(parseFloat(e.target.value) || 0)}
                fullWidth
                inputProps={{ step: 0.000001 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewWarehouseDialogOpen(false);
            setNewWarehouseName('');
            setNewWarehouseLat(0);
            setNewWarehouseLon(0);
            setEditingWarehouse(null);
          }}>{t('spareParts.cancel')}</Button>
          <Button 
            onClick={editingWarehouse ? handleUpdateWarehouse : handleCreateWarehouse} 
            variant="contained" 
            disabled={!newWarehouseName.trim() || isCreatingWarehouse || isUpdatingWarehouse}
          >
            {isCreatingWarehouse || isUpdatingWarehouse 
              ? (editingWarehouse ? t('spareParts.updating') : t('spareParts.adding')) 
              : (editingWarehouse ? t('spareParts.update') : t('spareParts.add'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('spareParts.usageHistoryTitle', { name: editingSparePart?.name || '' })}
        </DialogTitle>
        <DialogContent>
          {isHistoryLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : sparePartUsageHistory.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="body1" color="textSecondary">
                {t('spareParts.noUsageHistory')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t('spareParts.colDate')}</TableHeaderCell>
                    <TableHeaderCell>{t('spareParts.colScooter')}</TableHeaderCell>
                    <TableHeaderCell>{t('spareParts.colTaskDescription')}</TableHeaderCell>
                    <TableHeaderCell>{t('spareParts.colExecutor')}</TableHeaderCell>
                    <TableHeaderCell align="center">{t('spareParts.colQuantity')}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sparePartUsageHistory.map(item => {
                    const task = item.task;
                    const scooter = task.scooter as WorkshopTaskScooter;
                    const employee = task.employee as WorkshopTaskEmployee;
                    return (
                      <StyledTableRow key={item._id}>
                        <StyledTableCell>
                          {moment(task.finishedAt || task.updatedAt).format('DD.MM.YYYY HH:mm')}
                        </StyledTableCell>
                        <StyledTableCell>
                          {typeof scooter === 'object' ? scooter.name : scooter || '-'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {task.description || '-'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {typeof employee === 'object' 
                            ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.login 
                            : '-'}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Chip 
                            label={`-${item.qty}`} 
                            size="small" 
                            color="error" 
                            variant="outlined"
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="textSecondary" sx={{ mr: 'auto', ml: 2 }}>
            {t('spareParts.totalUsed', { count: sparePartUsageHistory.reduce((sum, item) => sum + item.qty, 0) })}
          </Typography>
          <Button onClick={() => setHistoryDialogOpen(false)}>{t('spareParts.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={photoModalOpen} 
        onClose={() => setPhotoModalOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme => theme.palette.mode === 'light' ? '#f5f5f5' : '#1a1a1a'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('spareParts.partPhoto')}</Typography>
            <IconButton onClick={() => setPhotoModalOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              maxHeight: '70vh'
            }}
          >
            <img
              src={photoModalSrc}
              alt={t('spareParts.partPhoto')}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Delete color="error" />
            <Typography variant="h6">{t('spareParts.deleteConfirmTitle')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('spareParts.deleteConfirmText')}
          </Typography>
          <Typography variant="body2" color="error">
            {t('spareParts.deleteIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} variant="outlined">
            {t('spareParts.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteCountdown > 0}
            startIcon={<Delete />}
          >
            {deleteCountdown > 0
              ? t('spareParts.deleteCountdown', { count: deleteCountdown })
              : t('spareParts.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sparePartTypesDialogOpen}
        onClose={() => {
          setSparePartTypesDialogOpen(false);
          setEditingSparePartType(null);
          setSparePartTypeName('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('spareParts.managePartTypes')}</DialogTitle>
        <DialogContent>
          <Box mt={2} display="flex" gap={1} mb={2}>
            <TextField
              label={editingSparePartType ? t('spareParts.editType') : t('spareParts.newType')}
              value={sparePartTypeName}
              onChange={e => setSparePartTypeName(e.target.value)}
              fullWidth
              size="small"
              placeholder={t('spareParts.typePlaceholder')}
            />
            {editingSparePartType ? (
              <>
                <Button
                  variant="contained"
                  onClick={handleUpdateSparePartType}
                  disabled={!sparePartTypeName.trim() || isUpdatingSparePartType}
                >
                  {isUpdatingSparePartType ? '...' : t('spareParts.save')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingSparePartType(null);
                    setSparePartTypeName('');
                  }}
                >
                  {t('spareParts.cancel')}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleCreateSparePartType}
                disabled={!sparePartTypeName.trim() || isCreatingSparePartType}
                startIcon={<Add />}
              >
                {isCreatingSparePartType ? '...' : t('spareParts.add')}
              </Button>
            )}
          </Box>
          <List>
            {sparePartTypesData.map(type => (
              <ListItem
                key={type._id}
                secondaryAction={
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingSparePartType(type);
                        setSparePartTypeName(type.name);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteSparePartType(type._id)}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText primary={type.name} />
              </ListItem>
            ))}
            {sparePartTypesData.length === 0 && (
              <ListItem>
                <ListItemText 
                  primary={t('spareParts.noPartTypes')}
                  secondary={t('spareParts.noPartTypesHint')}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSparePartTypesDialogOpen(false);
            setEditingSparePartType(null);
            setSparePartTypeName('');
          }}>
            {t('spareParts.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('spareParts.newOrderTitle')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label={t('spareParts.orderName')}
              value={orderFormData.name}
              onChange={e => setOrderFormData({ ...orderFormData, name: e.target.value })}
              fullWidth
              required
              placeholder={t('spareParts.orderNamePlaceholder')}
            />
            <TextField
              label={t('spareParts.orderDescription')}
              value={orderFormData.description}
              onChange={e => setOrderFormData({ ...orderFormData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Box display="flex" gap={2}>
              <TextField
                label={t('spareParts.trackingNumber')}
                value={orderFormData.trackingNumber}
                onChange={e => setOrderFormData({ ...orderFormData, trackingNumber: e.target.value })}
                fullWidth
                placeholder="Tracking number"
              />
              <TextField
                label={t('spareParts.expectedDeliveryDate')}
                type="date"
                value={orderFormData.expectedDeliveryDate}
                onChange={e => setOrderFormData({ ...orderFormData, expectedDeliveryDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            <Typography variant="subtitle1" fontWeight={600} mt={2}>
              {t('spareParts.orderParts')}
            </Typography>
            
            {orderFormData.spareParts.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{t('spareParts.colPart')}</TableHeaderCell>
                      <TableHeaderCell align="center">{t('spareParts.colQuantity')}</TableHeaderCell>
                      <TableHeaderCell align="center">{t('spareParts.colActions')}</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderFormData.spareParts.map(item => {
                      const sparePart = sparePartsData?.data.find(sp => sp._id === item.sparePart);
                      return (
                        <TableRow key={item.sparePart}>
                          <StyledTableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              {sparePart?.photo ? (
                                <Avatar 
                                  src={`${process.env.REACT_APP_API_URL || ''}/public/${sparePart.photo}`} 
                                  variant="rounded" 
                                  sx={{ width: 36, height: 36 }}
                                />
                              ) : (
                                <Avatar 
                                  variant="rounded" 
                                  sx={{ width: 36, height: 36, bgcolor: 'grey.300' }}
                                >
                                  <ImageIcon fontSize="small" />
                                </Avatar>
                              )}
                              <Box>
                                <Typography variant="body2">{sparePart?.name || item.sparePart}</Typography>
                                {sparePart?.code && (
                                  <Typography variant="caption" color="textSecondary">
                                    {sparePart.code}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <TextField
                              type="number"
                              value={item.quantity}
                              onChange={e => handleUpdateSparePartQuantity(item.sparePart, parseInt(e.target.value) || 1)}
                              size="small"
                              sx={{ width: 80 }}
                              inputProps={{ min: 1 }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveSparePartFromOrder(item.sparePart)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <FormControl fullWidth>
              <InputLabel>{t('spareParts.addPartBtn')}</InputLabel>
              <Select
                value=""
                label={t('spareParts.addPartBtn')}
                onChange={e => handleAddSparePartToOrder(e.target.value as string)}
              >
                {sparePartsData?.data
                  .filter(sp => !orderFormData.spareParts.some(item => item.sparePart === sp._id))
                  .map(sp => (
                    <MenuItem key={sp._id} value={sp._id}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        {sp.photo ? (
                          <Avatar 
                            src={`${process.env.REACT_APP_API_URL || ''}/public/${sp.photo}`} 
                            variant="rounded" 
                            sx={{ width: 32, height: 32 }}
                          />
                        ) : (
                          <Avatar 
                            variant="rounded" 
                            sx={{ width: 32, height: 32, bgcolor: 'grey.300' }}
                          >
                            <ImageIcon fontSize="small" />
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="body2">{sp.name}</Typography>
                          {sp.code && (
                            <Typography variant="caption" color="textSecondary">
                              {sp.code}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>{t('spareParts.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreateOrder}
            disabled={isCreatingOrder || !orderFormData.name.trim() || orderFormData.spareParts.length === 0}
            startIcon={isCreatingOrder ? <CircularProgress size={16} /> : <LocalShipping />}
          >
            {t('spareParts.createOrder')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ordersListDialogOpen} onClose={() => setOrdersListDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <LocalShipping color="primary" />
              <Typography variant="h6">{t('spareParts.ordersInTransit')}</Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<Add />}
              onClick={() => {
                setOrdersListDialogOpen(false);
                handleOpenOrderDialog();
              }}
            >
              {t('spareParts.newOrder')}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isOrdersLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : !ordersData?.data || ordersData.data.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="body1" color="textSecondary">
                {t('spareParts.noActiveOrders')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t('spareParts.name')}</TableHeaderCell>
                    <TableHeaderCell>{t('spareParts.colParts')}</TableHeaderCell>
                    <TableHeaderCell>{t('spareParts.colTracking')}</TableHeaderCell>
                    <TableHeaderCell>{t('spareParts.colExpectedDate')}</TableHeaderCell>
                    <TableHeaderCell align="center">{t('spareParts.colActions')}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersData.data.map(order => (
                    <StyledTableRow key={order._id}>
                      <StyledTableCell>
                        <Typography variant="body2" fontWeight={500}>{order.name}</Typography>
                        {order.description && (
                          <Typography variant="caption" color="textSecondary">
                            {order.description}
                          </Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {order.spareParts.map((item, idx) => {
                            const sparePartData = typeof item.sparePart === 'object' 
                              ? item.sparePart 
                              : sparePartsData?.data.find(sp => sp._id === item.sparePart);
                            return (
                              <Typography key={idx} variant="caption">
                                {typeof sparePartData === 'object' ? sparePartData?.name : item.sparePart}: {item.quantity} {t('spareParts.pcs')}
                              </Typography>
                            );
                          })}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        {order.trackingNumber || '-'}
                      </StyledTableCell>
                      <StyledTableCell>
                        {order.expectedDeliveryDate ? moment(order.expectedDeliveryDate).format('DD.MM.YYYY') : '-'}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleDeliverOrder(order._id)}
                            title={t('spareParts.markAsDelivered')}
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelOrder(order._id)}
                            title={t('spareParts.cancelOrder')}
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrdersListDialogOpen(false)}>{t('spareParts.close')}</Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default SparePartsPage;
